<script setup>
import { ref, nextTick, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { required, email } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Testimonials from '../auth/signup/components/Testimonials/Index.vue';

// components
import FormInput from '../../components/Form/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  authError: {
    type: String,
    default: '',
  },
  target: {
    type: String,
    default: 'web',
  },
});

const store = useStore();
const { t } = useI18n();

const credentials = ref({
  email: '',
});

const loginApi = ref({
  showLoading: false,
  hasErrored: false,
});

const handleAuthError = () => {
  if (!props.authError) {
    return;
  }

  const translatedMessage = t('LOGIN.SAML.API.ERROR_MESSAGE');
  useAlert(translatedMessage);
  loginApi.value.hasErrored = true;
};

const validations = {
  credentials: {
    email: {
      required,
      email,
    },
  },
};

const v$ = useVuelidate(validations, { credentials });

const globalConfig = computed(() => store.getters['globalConfig/get']);
const isAEasyDeskInstance = computed(
  () => globalConfig.value?.installationName === 'EasyDesk'
);
const csrfToken = ref('');

onMounted(async () => {
  csrfToken.value =
    document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute('content') || '';

  await nextTick(handleAuthError);
});
</script>

<template>
  <main
    class="relative min-h-screen overflow-hidden bg-gradient-to-r from-n-amber-3 via-white to-white dark:from-n-background dark:via-n-solid-2 dark:to-n-solid-3"
  >
    <div
      class="pointer-events-none absolute -top-40 left-1/2 h-80 w-[36rem] -translate-x-1/2 rounded-full bg-n-amber-5/50 blur-[160px] dark:bg-n-brand/10"
    />
    <div
      class="pointer-events-none absolute bottom-[-14rem] right-[-6rem] h-96 w-[34rem] rounded-full bg-n-amber-7/40 blur-[180px] dark:bg-n-blue-5/20"
    />

    <div class="relative flex min-h-screen flex-col-reverse lg:flex-row">
      <Testimonials v-if="isAEasyDeskInstance" class="hidden flex-1 lg:flex" />

      <section
        class="relative flex flex-1 items-center justify-center px-6 py-16 sm:px-10 lg:px-20"
      >
        <div class="relative z-10 w-full max-w-lg">
          <div class="mb-12 space-y-8">
            <div class="flex items-center gap-3">
              <img
                :src="globalConfig.logo"
                :alt="globalConfig.installationName"
                class="block h-9 w-auto shrink-0 dark:hidden"
              />
              <img
                v-if="globalConfig.logoDark"
                :src="globalConfig.logoDark"
                :alt="globalConfig.installationName"
                class="hidden h-9 w-auto shrink-0 dark:block"
              />
            </div>
            <span
              class="inline-flex items-center gap-2 rounded-full bg-n-amber-4 px-4 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-n-amber-11 shadow-sm ring-1 ring-n-amber-5/60"
            >
              <span class="i-lucide-rocket h-3.5 w-3.5" />
              {{ t('LOGIN.INTRO_BADGE') }}
            </span>

            <div class="space-y-4">
              <h1 class="text-4xl font-bold leading-tight text-n-slate-12">
                {{ t('LOGIN.SAML.TITLE') }}
              </h1>
              <p class="text-base text-n-slate-11">
                {{ t('LOGIN.SAML.SUBTITLE') }}
              </p>
            </div>
          </div>

          <div
            class="relative overflow-hidden rounded-3xl border border-n-amber-4 bg-white/95 p-8 shadow-[0_32px_96px_-48px_rgba(255,106,0,0.65)] backdrop-blur-xl ring-1 ring-n-amber-4/70 transition-all duration-300 dark:border-n-container/30 dark:bg-n-solid-2/90 dark:ring-n-container sm:p-10"
            :class="{ 'animate-wiggle': loginApi.hasErrored }"
          >
            <div
              class="pointer-events-none absolute inset-x-10 -top-24 h-32 rounded-full bg-n-amber-5/50 blur-3xl dark:bg-n-brand/5"
            />
            <div class="relative space-y-6">
              <form
                class="space-y-5"
                method="POST"
                action="/api/v1/auth/saml_login"
              >
                <FormInput
                  v-model="credentials.email"
                  name="email"
                  type="text"
                  :tabindex="1"
                  required
                  :label="t('LOGIN.SAML.WORK_EMAIL.LABEL')"
                  :placeholder="t('LOGIN.SAML.WORK_EMAIL.PLACEHOLDER')"
                  :has-error="v$.credentials.email.$error"
                  @input="v$.credentials.email.$touch"
                />
                <input
                  type="hidden"
                  class="h-0"
                  name="authenticity_token"
                  :value="csrfToken"
                />
                <input
                  type="hidden"
                  class="h-0"
                  name="target"
                  :value="target"
                />
                <NextButton
                  lg
                  amber
                  type="submit"
                  class="w-full"
                  :tabindex="2"
                  :label="t('LOGIN.SAML.SUBMIT')"
                  :disabled="loginApi.showLoading"
                  :is-loading="loginApi.showLoading"
                />
              </form>

              <div class="text-center text-sm text-n-slate-11">
                <router-link
                  to="/app/login"
                  class="inline-flex items-center justify-center gap-2 rounded-full bg-n-amber-3 px-4 py-2 font-medium text-n-amber-11 shadow-sm ring-1 ring-inset ring-n-amber-5 transition hover:-translate-y-0.5 hover:bg-n-amber-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-amber-8 focus-visible:ring-offset-2 dark:bg-n-solid-3 dark:text-n-slate-12 dark:ring-n-container dark:hover:bg-n-alpha-2"
                >
                  <span class="i-lucide-arrow-left h-4 w-4" />
                  {{ t('LOGIN.SAML.BACK_TO_LOGIN') }}
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </main>
</template>
